import React, { useEffect, useState, useRef } from 'react';
import { PlusIcon, TagIcon } from 'lucide-react';
import { Tag, TagColor } from './Tag';
export interface TagData {
  id: string;
  label: string;
  color: TagColor;
}
export interface TagInputProps {
  tags: TagData[];
  availableTags: TagData[];
  onAddTag: (tag: TagData) => void;
  onRemoveTag: (tagId: string) => void;
  onCreateTag?: (label: string, color?: string) => void;
  placeholder?: string;
  'data-id'?: string;
}
export function TagInput({
  tags,
  availableTags,
  onAddTag,
  onRemoveTag,
  onCreateTag,
  placeholder = 'Add tag...',
  'data-id': dataId
}: TagInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string>('blue');
  const [customColor, setCustomColor] = useState('#0EA5E9');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Color options
  const colorOptions: Array<{
    value: 'blue' | 'green' | 'orange' | 'purple' | 'gray';
    label: string;
    preview: string;
  }> = [{
    value: 'blue',
    label: 'Blue',
    preview: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]'
  }, {
    value: 'green',
    label: 'Green',
    preview: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]'
  }, {
    value: 'orange',
    label: 'Orange',
    preview: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]'
  }, {
    value: 'purple',
    label: 'Purple',
    preview: 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]'
  }, {
    value: 'gray',
    label: 'Gray',
    preview: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'
  }];
  // Get inline styles for custom colors
  const getCustomColorStyles = (color: string) => {
    if (!color.startsWith('#')) return {};
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    const rgb = hexToRgb(color);
    if (!rgb) return {};
    return {
      backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
      color: color,
      borderColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`
    };
  };
  // Filter available tags that aren't already added
  const unusedTags = availableTags.filter(available => !tags.some(t => t.id === available.id));
  // Filter by search input
  const filteredTags = unusedTags.filter(tag => tag.label.toLowerCase().includes(inputValue.toLowerCase()));
  // Check if input matches an existing tag
  const exactMatch = availableTags.find(tag => tag.label.toLowerCase() === inputValue.toLowerCase());
  const canCreateNew = inputValue.trim() && onCreateTag && !exactMatch;
  const hasFilteredResults = filteredTags.length > 0;
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowColorPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelectTag = (tag: TagData) => {
    onAddTag(tag);
    setInputValue('');
    setIsOpen(false);
    setShowColorPicker(false);
  };
  const handleCreateNew = () => {
    if (canCreateNew) {
      onCreateTag(inputValue.trim(), selectedColor);
      setInputValue('');
      setIsOpen(false);
      setShowColorPicker(false);
      setSelectedColor('blue');
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showColorPicker) {
        handleCreateNew();
      } else if (hasFilteredResults) {
        handleSelectTag(filteredTags[0]);
      } else if (canCreateNew) {
        setShowColorPicker(true);
      }
    } else if (e.key === 'Escape') {
      if (showColorPicker) {
        setShowColorPicker(false);
      } else {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    }
  };
  const openInput = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  return <div ref={containerRef} className="relative" data-id="element-5854" data-mp="3y13z0zypjvw">
      {/* Current tags */}
      <div className="flex flex-wrap items-center gap-2" data-id="element-5855" data-mp="8yp2s8ufn50o">
        {tags.map(tag => <Tag key={tag.id} label={tag.label} color={tag.color} onRemove={() => onRemoveTag(tag.id)} data-id="element-5856" data-mp="czqum4m0sesm" />)}

        {/* Add tag button/input */}
        <div className="relative" data-id="element-5857" data-mp="htlm3u7v9y29">
          {!isOpen ? <button type="button" onClick={openInput} aria-label="Add tag" className="
                inline-flex items-center gap-1.5 h-8 px-3 rounded-md
                text-sm font-medium
                border border-dashed border-[#CBD5E1] text-[#94A3B8]
                hover:border-[#0EA5E9] hover:text-[#0EA5E9] hover:bg-[#E0F2FE]
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20
              " data-id="element-5858" data-mp="mqv8f0zyltlv">







              <PlusIcon className="w-3.5 h-3.5" data-id="element-5859" data-mp="278e5qahnt0ah" />
              <span data-id="element-5860" data-mp="27961blb9ay0r">Add tag</span>
            </button> : <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder={placeholder} aria-label="Search or create tag" aria-expanded={isOpen} aria-controls="tag-dropdown" className="
                w-40 h-8 px-3 text-sm rounded-md
                border border-[#0EA5E9] bg-white
                focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20
                text-[#0F172A] placeholder-[#94A3B8]
              " data-id="element-5861" data-mp="229t549yhweli" />}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && <div id="tag-dropdown" role="listbox" className="
            absolute z-50 mt-2 w-80 py-2 bg-white rounded-lg
            border border-[#E2E8F0] shadow-lg
          " data-id="element-5862" data-mp="3ip45barkutv4">



          {showColorPicker ?
      // Color Picker View
      <div className="px-4 py-3" data-id="element-5863" data-mp="1gyngvedc02i0">
              <div className="mb-3" data-id="element-5864" data-mp="1gxly0nq2qbpq">
                <p className="text-[12px] font-medium text-[#475569] mb-2" data-id="element-5865" data-mp="1h0qae8dp0drh">
                  Choose a color for "{inputValue}"
                </p>
              </div>

              {/* Preset Colors */}
              <div className="grid grid-cols-5 gap-2 mb-3" data-id="element-5866" data-mp="176hgbg90kcr7">
                {colorOptions.map(option => <button key={option.value} onClick={() => setSelectedColor(option.value)} className={`p-2 rounded-md border-2 transition-all ${selectedColor === option.value ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9] ring-opacity-20' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-5867" data-mp="tb5mrc6lgsbv">
                    <span className={`inline-flex items-center justify-center w-full px-2 py-1 rounded text-xs font-medium border ${option.preview}`} data-id="element-5868" data-mp="oahuyoajk7gl">
                      {option.label.charAt(0)}
                    </span>
                  </button>)}
              </div>

              {/* Custom Color */}
              <div className="mb-3 p-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md" data-id="element-5869" data-mp="70x6qdgtcf5v">
                <label className="flex items-center gap-2" data-id="element-5870" data-mp="23441uepy3ac">
                  <input type="color" value={selectedColor.startsWith('#') ? selectedColor : customColor} onChange={e => {
              setCustomColor(e.target.value);
              setSelectedColor(e.target.value);
            }} className="w-8 h-8 rounded border border-[#CBD5E1] cursor-pointer" data-id="element-5871" data-mp="gqytn1xfjrj7" />
                  <span className="text-[12px] text-[#475569]" data-id="element-5872" data-mp="kstwiav0ecz3">Custom</span>
                  {selectedColor.startsWith('#') && <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border" style={getCustomColorStyles(selectedColor)} data-id="element-5873" data-mp="zo9uus2mgv1a">
                      Preview
                    </span>}
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-2" data-id="element-5874" data-mp="2wid25e71rkyf">
                <button onClick={handleCreateNew} className="flex-1 px-3 py-2 bg-[#0EA5E9] text-white text-[13px] font-medium rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-5875" data-mp="2wjft1m4ddnec">
                  Create Tag
                </button>
                <button onClick={() => setShowColorPicker(false)} className="px-3 py-2 bg-white border border-[#CBD5E1] text-[#475569] text-[13px] font-medium rounded-md hover:bg-[#F1F5F9] transition-colors" data-id="element-5876" data-mp="2keh5668ecngr">
                  Back
                </button>
              </div>
            </div> : hasFilteredResults ? <div className="max-h-48 overflow-y-auto" data-id="element-5877" data-mp="1woeenuk4rpk9">
              {filteredTags.map(tag => <button key={tag.id} type="button" role="option" onClick={() => handleSelectTag(tag)} className="
                    w-full px-3 py-2 text-left flex items-center gap-2
                    hover:bg-[#F1F5F9] transition-colors
                    focus:outline-none focus:bg-[#F1F5F9]
                  " data-id="element-5878" data-mp="1wqhe1576av1n">




                  <TagIcon className="w-4 h-4 text-[#94A3B8]" data-id="element-5879" data-mp="3h3yi969plbsh" />
                  <Tag label={tag.label} color={tag.color} data-id="element-5880" data-mp="3h6kn2y57td70" />
                </button>)}
            </div> : inputValue.trim() ? <div className="px-3 py-2" data-id="element-5881" data-mp="11fj79h6znhos">
              {canCreateNew ? <button type="button" onClick={() => setShowColorPicker(true)} className="
                    w-full px-3 py-2 text-left flex items-center gap-2
                    hover:bg-[#F1F5F9] rounded-md transition-colors
                    text-[#0EA5E9] font-medium text-sm
                    focus:outline-none focus:bg-[#F1F5F9]
                  " data-id="element-5882" data-mp="11g2r53g78g5g">





                  <PlusIcon className="w-4 h-4" data-id="element-5883" data-mp="hragnbl3fjre" />
                  <span data-id="element-5884" data-mp="hto9ddsawjvs">Create "{inputValue}"</span>
                </button> : <p className="text-sm text-[#94A3B8]" data-id="element-5885" data-mp="dqzxsyc7cyv4">No matching tags</p>}
            </div> : <p className="px-3 py-2 text-sm text-[#94A3B8]" data-id="element-5886" data-mp="8rey2lp7kmhc">
              No more tags available
            </p>}
        </div>}
    </div>;
}